import { useMemo } from "react";

import { useHistory } from "react-router-dom";
import { Stack } from "@mui/material";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import { AmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import { CardPager } from "../CardPager";
import EmptyList from "../EmptyList";
import HomePageCard from "../HomePageCard";
import { AcknolagedAnomalyRow } from "../Rows/AcknolagedAnomalyRow";
import { type AnomalyWithCostAndRef } from "../types";
import { ITEMS_PER_PAGE, usePagination } from "./hooks";

type AcknowledgedAnomaliesListProps = {
  acknowledgedAnomalies: AnomalyWithCostAndRef[];
};

const AcknowledgedAnomaliesList = ({ acknowledgedAnomalies }: AcknowledgedAnomaliesListProps) => {
  const history = useHistory();

  const totalCost = useMemo(
    () => acknowledgedAnomalies.reduce((acc, anomaly) => acc + anomaly.cost, 0),
    [acknowledgedAnomalies]
  );

  const { currentPageItems, currentPage, startItem, endItem, totalItems, handlePageChange } =
    usePagination(acknowledgedAnomalies);

  if (acknowledgedAnomalies.length === 0) {
    return (
      <EmptyList
        title="Acknowledged anomalies"
        subheader="Reduce risk by taking action and acknowledging anomalies"
        description="You haven’t acknowledged any anomalies yet"
        buttonText="View anomalies"
        buttonDataCy="implemented-anomalies-btn"
        onButtonClick={() => {
          history.push("./monitor/cost-anomalies");
        }}
      />
    );
  }

  return (
    <HomePageCard
      title="Acknowledged anomalies"
      subheader={
        <Typography variant="body2" color="text.secondary">
          You have saved{" "}
          <b>
            <AmountInCustomerCurrency amount={totalCost} />
          </b>{" "}
          by taking action and acknowledging anomalies
        </Typography>
      }
    >
      <Stack sx={{ minHeight: 479, height: "100%", justifyContent: "space-between" }}>
        <List dense sx={{ pb: 0, pt: 0 }}>
          {currentPageItems.map((anomaly, index) => (
            <AcknolagedAnomalyRow
              key={`${anomaly.metadata.sku_name}-${index}`}
              anomaly={anomaly}
              hideDivider={index === currentPageItems.length - 1 && currentPageItems.length === ITEMS_PER_PAGE}
            />
          ))}
        </List>
        <CardPager
          startItem={startItem}
          endItem={endItem}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </Stack>
    </HomePageCard>
  );
};

export default AcknowledgedAnomaliesList;
